import { batch, observable } from "@legendapp/state";
import type { FormattedMeetingNotes } from "@/types/fireflies";

export type BatchFormatResultItem = {
  transcriptId: string;
  meetingTitle: string;
  status: "success" | "failed";
  error?: string;
  notes?: FormattedMeetingNotes;
  editableTitle: string;
  selectedForClickUp: boolean;
  suggestedAssigneeId: number | null;
  assigneeId: number | null;
  assigneeReason?: string;
};

export type BatchProcessingState = {
  active: boolean;
  currentTranscriptId: string | null;
  currentLabel: string;
  completed: number;
  total: number;
};

export interface FirefliesUiState {
  selectedTranscriptId: string | null;
  previewOpen: boolean;
  formattedPreview: FormattedMeetingNotes | null;
  editableTitle: string;
  batchResultsOpen: boolean;
  batchResults: BatchFormatResultItem[];
  activeBatchResultId: string | null;
  batchProcessing: BatchProcessingState | null;
  selectedClickUpTeamId: string | null;
  selectedClickUpListId: string | null;
  clickUpSubmitting: boolean;
}

export const firefliesUi$ = observable<FirefliesUiState>({
  selectedTranscriptId: null,
  previewOpen: false,
  formattedPreview: null,
  editableTitle: "",
  batchResultsOpen: false,
  batchResults: [],
  activeBatchResultId: null,
  batchProcessing: null,
  selectedClickUpTeamId: null,
  selectedClickUpListId: null,
  clickUpSubmitting: false,
});

export function selectFirefliesTranscript(id: string | null): void {
  firefliesUi$.selectedTranscriptId.set(id);
}

/** @deprecated Use openFirefliesBatchResults for single + batch flows */
export function openFirefliesPreview(formatted: FormattedMeetingNotes): void {
  openFirefliesBatchResults([
    {
      transcriptId: formatted.structured.firefliesTranscriptId,
      meetingTitle: formatted.title,
      status: "success",
      notes: formatted,
      editableTitle: formatted.parentTaskTitle,
      selectedForClickUp: true,
      suggestedAssigneeId: null,
      assigneeId: null,
    },
  ]);
}

export function openFirefliesBatchResults(
  results: BatchFormatResultItem[],
  options?: { processing?: BatchProcessingState | null }
): void {
  const firstSuccess = results.find((r) => r.status === "success");
  const firstAny = results[0];
  batch(() => {
    firefliesUi$.batchResults.set(results);
    firefliesUi$.activeBatchResultId.set(
      firstSuccess?.transcriptId ?? firstAny?.transcriptId ?? null
    );
    firefliesUi$.batchProcessing.set(options?.processing ?? null);
    firefliesUi$.batchResultsOpen.set(true);
    firefliesUi$.previewOpen.set(false);
  });
}

export function appendBatchFormatResult(item: BatchFormatResultItem): void {
  const current = firefliesUi$.batchResults.peek();
  const existing = current.findIndex((r) => r.transcriptId === item.transcriptId);
  const next =
    existing >= 0
      ? current.map((r, i) => (i === existing ? item : r))
      : [...current, item];
  firefliesUi$.batchResults.set(next);
  if (item.status === "success" && !firefliesUi$.activeBatchResultId.peek()) {
    firefliesUi$.activeBatchResultId.set(item.transcriptId);
  }
}

export function setBatchProcessing(state: BatchProcessingState | null): void {
  firefliesUi$.batchProcessing.set(state);
}

export function setActiveBatchResult(transcriptId: string): void {
  firefliesUi$.activeBatchResultId.set(transcriptId);
}

export function updateBatchResultTitle(transcriptId: string, title: string): void {
  const current = firefliesUi$.batchResults.peek();
  firefliesUi$.batchResults.set(
    current.map((r) =>
      r.transcriptId === transcriptId ? { ...r, editableTitle: title } : r
    )
  );
}

export function updateBatchResultAssignee(transcriptId: string, assigneeId: number | null): void {
  const current = firefliesUi$.batchResults.peek();
  firefliesUi$.batchResults.set(
    current.map((r) =>
      r.transcriptId === transcriptId ? { ...r, assigneeId } : r
    )
  );
}

export function setSelectedClickUpTeamId(teamId: string | null): void {
  firefliesUi$.selectedClickUpTeamId.set(teamId);
  firefliesUi$.selectedClickUpListId.set(null);
}

export function setSelectedClickUpListId(listId: string | null): void {
  firefliesUi$.selectedClickUpListId.set(listId);
}

export function setClickUpSubmitting(submitting: boolean): void {
  firefliesUi$.clickUpSubmitting.set(submitting);
}

export function toggleBatchResultClickUpSelection(transcriptId: string): void {
  const current = firefliesUi$.batchResults.peek();
  firefliesUi$.batchResults.set(
    current.map((r) =>
      r.transcriptId === transcriptId && r.status === "success"
        ? { ...r, selectedForClickUp: !r.selectedForClickUp }
        : r
    )
  );
}

export function toggleAllBatchResultsClickUpSelection(selected: boolean): void {
  const current = firefliesUi$.batchResults.peek();
  firefliesUi$.batchResults.set(
    current.map((r) =>
      r.status === "success" ? { ...r, selectedForClickUp: selected } : r
    )
  );
}

export function closeFirefliesPreview(): void {
  firefliesUi$.previewOpen.set(false);
}

export function closeFirefliesBatchResults(): void {
  firefliesUi$.batchResultsOpen.set(false);
  firefliesUi$.batchProcessing.set(null);
  firefliesUi$.clickUpSubmitting.set(false);
}

export function resetFirefliesPreview(): void {
  batch(() => {
    firefliesUi$.formattedPreview.set(null);
    firefliesUi$.editableTitle.set("");
    firefliesUi$.batchResults.set([]);
    firefliesUi$.activeBatchResultId.set(null);
    firefliesUi$.batchProcessing.set(null);
  });
}
